"use client";

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { SiteNav } from "trimble-terra/SiteNav";
import dynamic from "next/dynamic";
const NavSearch = dynamic(() => import("./NavSearch/NavSearch"), {
  ssr: false,
  loading: () => null
});
import { useRouter, usePathname, useParams, useSearchParams } from "next/navigation";
import NoPrefetchNavLink from "./NoPrefetchNavLink";
import useMouse from "@/components/terra/utils/hooks/useMouse";
import PersonalizedNavigationCategory from "./PersonalizedNavigationCategory";
import PersonalizedMobileNavigationCategory from "./PersonalizedMobileNavigationCategory";
import { formatCategoryLinks } from "./FormatNavCategory";
import { cleanURLWithLang } from "@/lib/url/cleanURLWithLang";
import { resolveImageLinkHref } from "@/lib/formatters/formatLinks";
import { isNavigationItemLink, filterMenuItemsForStrictLocale } from "@/lib/formatters/formatNavCategoryVariant";
import useScrollLock from "@/lib/useScrollLock";
import { parseFilterTokens, rebuildFilter } from "./NavSearch/ActiveSearchFilters";
import { createIsPageTranslated } from "@/lib/locale/strictMode";
import { toContentfulLocale } from "@/lib/locale/toContentfulLocale";
export default function FormatSiteNavigation(props: any) {
  const {
    data,
    _profile,
    config,
    siteLangs,
    handleSectorFlyoutDeployed,
    isSectorFlyoutDeployed,
    sectorFlyoutData,
    isAuthenticated,
    flyoutCart,
    rawAppData,
    setPageTopPadding,
    isStrictLocale = false
  } = props;
  const params = useParams();
  const lang = params?.lang as string;
  const locale = lang || "en";
  const [currentOpenMegaMenu, setCurrentOpenMegaMenu] = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useScrollLock(mobileNavOpen);
  const [isHidden, setIsHidden] = useState(false);
  const [languageSelectorDeployed, setLanguageSelectorDeployed] = useState(false);
  const [globalSearch, setGlobalSearch] = useState(false);
  const [openTopLevelMobileItem, setOpenTopLevelMobileItem] = useState<string | null>(null);
  const [openSecondLevelMobileItem, setOpenSecondLevelMobileItem] = useState<string | null>(null);
  const [_currentOpenMenu, setCurrentOpenMenu] = useState(null);
  const [_hamburgerIsOpen, setHamburgerIsOpen] = useState(false);
  const [waffleMenuDeployed, setWaffleMenuDeployed] = useState(false);
  const [currentOpenSubCategory, setCurrentOpenSubCategory] = useState<string | null>(null);
  const [navMinHeight, setNavMinHeight] = useState(550);
  const [isSubCategoryOpen, setIsSubCategoryOpen] = useState<boolean | null>(false);
  const [_sectorFlyoutDeployed, setSectorFlyoutDeployed] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const urlSearchParams = useSearchParams();
  const search = urlSearchParams?.get("search") ?? null;
  const searchFilterParam = urlSearchParams?.get("searchFilter") ?? null;
  const searchIndexParam = urlSearchParams?.get("searchIndex") ?? null;
  const navRef = useRef<any>(null);
  const [lastPosition, _setLastPosition] = useState(100);
  const lastPositionRef = useRef(lastPosition);
  const {
    mouse
  } = useMouse();
  const [sectorFlyoutOpen, setSectorFlyoutOpen] = useState(false);
  const setLastPosition = (data: number) => {
    lastPositionRef.current = data;
    _setLastPosition(data);
  };
  const handleLanguageSelectorDeployed = () => {
    setLanguageSelectorDeployed(!languageSelectorDeployed);
    closeAllDropDowns();
  };
  const [searchDeployed, setSearchDeployed] = useState(!!search);
  const [desktopSearchDeployed, setDesktopSearchDeployed] = useState(searchDeployed);
  const [hasSearchActivated, setHasSearchActivated] = useState(!!search);
  const [currentRefinement, setCurrentRefinement] = useState(search || "");
  const [activeSearchFilter, setActiveSearchFilter] = useState<string | null>(searchFilterParam);
  const [activeSearchIndex, setActiveSearchIndex] = useState<string | null>(searchIndexParam);

  // Tracks the `search` value we most recently pushed to the URL ourselves
  // (via router.push/replace below). useSearchParams() only reflects a URL
  // update asynchronously, one or more renders after we requested it. If the
  // user keeps typing in that window, the eventual echo of our own stale
  // push would otherwise land here and overwrite the newer, already-typed
  // text — reverting the latest keystroke(s). Skipping self-echoes (while
  // still honoring genuinely external URL changes, e.g. a deep link or
  // browser back/forward) avoids that race.
  const lastPushedSearchRef = useRef<string | null>(search || null);
  useEffect(() => {
    // open search if query param is present
    if (search) {
      setSearchDeployed(true);
      if (search !== lastPushedSearchRef.current) {
        setCurrentRefinement(search);
      }
    }
    if (searchFilterParam) setActiveSearchFilter(searchFilterParam);
    if (searchIndexParam) setActiveSearchIndex(searchIndexParam);
  }, [search, searchFilterParam, searchIndexParam]);
  useEffect(() => {
    const htmlElem = document?.documentElement?.classList;
    if (searchDeployed === true) {
      htmlElem?.add("ter-app--scroll-locked");
      setIsHidden(false);
    }
    if (!searchDeployed) {
      htmlElem?.remove("ter-app--scroll-locked");
    }
    return () => htmlElem.remove("ter-app--scroll-locked");
  }, [searchDeployed]);
  const [searchQuery, setSearchQuery] = useState(search || undefined);
  const preloadNavSearch = useCallback(() => {
    void import("./NavSearch/NavSearch");
  }, []);
  const activateNavSearch = useCallback(() => {
    preloadNavSearch();
    setHasSearchActivated(true);
    setSearchDeployed(true);
    setDesktopSearchDeployed(true);
  }, [preloadNavSearch]);
  const onSearch = (value: string) => {
    activateNavSearch();
    setCurrentRefinement(value);
    lastPushedSearchRef.current = value;
    router?.push(`${pathname}?search=${encodeURIComponent(value)}`);
  };
  useEffect(() => {
    setSearchDeployed(desktopSearchDeployed);
  }, [desktopSearchDeployed]);
  useEffect(() => {
    setDesktopSearchDeployed(searchDeployed);
  }, [searchDeployed]);
  useEffect(() => {
    const navElement = navRef?.current;
    if (!navElement) return;
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        setPageTopPadding(entry.borderBoxSize?.[0]?.blockSize ?? navElement.offsetHeight);
      }
    });
    observer.observe(navElement);
    return () => observer.disconnect();
  }, [setPageTopPadding]);
  const cleanURL = (item: any) => cleanURLWithLang(item, lang);
  const contentfulLocale = toContentfulLocale(locale);
  const isPageTranslated = useMemo(() => createIsPageTranslated(contentfulLocale, isStrictLocale), [contentfulLocale, isStrictLocale]);
  let navCtaData: any = {
    text: data?.navCta?.title || "",
    url: cleanURL(data?.navCta)
  };
  if (navCtaData.text == "") {
    navCtaData = null;
  }
  useEffect(() => {
    if (searchQuery?.length && searchQuery?.length >= 2) {
      setCurrentRefinement(`${searchQuery}`);
      // Only update the URL when it doesn't already reflect the query.
      // Without this guard, onSearch's router.push triggers this effect via the
      // URL-param → currentRefinement → setSearchQuery cascade, causing a second
      // router call that produces a visible reload loop on iPhone/Safari.
      if (search !== searchQuery) {
        lastPushedSearchRef.current = searchQuery;
        router?.replace(`${pathname}?search=${encodeURIComponent(searchQuery)}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);
  const clearSearch = useCallback(() => {
    setSearchQuery(undefined);
    setCurrentRefinement("");
    setActiveSearchFilter(null);
    setActiveSearchIndex(null);
    lastPushedSearchRef.current = null;
    router?.replace(pathname);
    setSearchDeployed(false);
  }, [pathname, router]);
  const removeSearchToken = useCallback((tokenIndex: number) => {
    if (!activeSearchFilter) return;
    const tokens = parseFilterTokens(activeSearchFilter);
    tokens.splice(tokenIndex, 1);
    const params = new URLSearchParams(urlSearchParams?.toString() ?? "");
    if (tokens.length === 0) {
      setActiveSearchFilter(null);
      params.delete("searchFilter");
    } else {
      const updated = rebuildFilter(tokens);
      setActiveSearchFilter(updated);
      params.set("searchFilter", updated);
    }
    const qs = params.toString();
    router?.replace(qs ? `${pathname}?${qs}` : pathname);
  }, [activeSearchFilter, pathname, router, urlSearchParams]);
  const clearSearchFilter = useCallback(() => {
    setActiveSearchFilter(null);
    setActiveSearchIndex(null);
    const params = new URLSearchParams(urlSearchParams?.toString() ?? "");
    params.delete("searchFilter");
    params.delete("searchIndex");
    const qs = params.toString();
    router?.replace(qs ? `${pathname}?${qs}` : pathname);
  }, [pathname, router, urlSearchParams]);

  // const baseUrl = profile.BASE_URL ? profile.BASE_URL : "https://www.trimble.com";

  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (searchDeployed === false && desktopSearchDeployed === false) {
      clearSearch();
    }
  }, [searchDeployed, desktopSearchDeployed, clearSearch]);
  const formattedMainNavItems = useMemo(() => {
    return data?.navigationItemsCollection?.items?.filter((navItem: any) => {
      if (!navItem) return false;
      if (isNavigationItemLink(navItem)) {
        return isPageTranslated(navItem?.pageLink);
      }
      return true;
    })?.map((navItem: any, _index: number) => {
      if (navItem.__typename === "NavigationMenu") {
        // toplevel category
        const filteredNavItems = isStrictLocale ? filterMenuItemsForStrictLocale(navItem?.navigationItemsCollection?.items, isPageTranslated) : navItem?.navigationItemsCollection?.items?.filter(Boolean);
        const links = formatCategoryLinks(filteredNavItems, locale, cleanURL, isStrictLocale ? isPageTranslated : undefined);
        if (isStrictLocale && (!links || links.length === 0)) return null;
        return {
          contentfulId: navItem?.sys?.id,
          text: navItem?.title,
          url: cleanURL(navItem),
          type: filteredNavItems?.length > 0 ? "category" : "link",
          id: navItem?.sys?.id,
          LinkComponent: NoPrefetchNavLink,
          locale: locale,
          categoryLink: {
            text: navItem?.categoryLink?.title,
            description: navItem?.categoryLink?.description,
            url: navItem?.categoryLink?.pageLink?.slug ? cleanURL(navItem?.categoryLink) : undefined
          },
          navCategoryPromo: navItem?.featuredPromo?.title !== null && navItem?.featuredPromo?.title !== undefined ? {
            title: navItem?.featuredPromo?.title,
            description: navItem?.featuredPromo?.description,
            pageLink: {
              url: cleanURL(navItem?.featuredPromo),
              text: navItem?.featuredPromo?.pageLinkLabel || navItem?.featuredPromo?.pageLink?.text
            },
            image: {
              src: navItem?.featuredPromo?.image?.image?.url,
              altText: navItem?.featuredPromo?.image?.altText
            }
          } : null,
          links,
          personalizedComponent: PersonalizedNavigationCategory,
          personalizedMobileComponent: PersonalizedMobileNavigationCategory,
          ntExperiencesCollection: navItem?.ntExperiencesCollection ? {
            items: navItem?.ntExperiencesCollection?.items || []
          } : {
            items: null
          }
        };
      } else {
        return {
          contentfulId: navItem?.sys?.id,
          text: navItem?.title,
          url: cleanURL(navItem),
          type: navItem?.navigationItemsCollection?.items?.length > 0 ? "category" : "link",
          LinkComponent: NoPrefetchNavLink,
          locale: locale,
          categoryLink: {
            text: navItem?.categoryLink?.title,
            description: navItem?.categoryLink?.description,
            url: navItem?.categoryLink?.pageLink?.slug ? cleanURL(navItem?.categoryLink) : undefined
          },
          navCategoryPromo: {
            title: navItem?.featuredPromo?.title,
            description: navItem?.featuredPromo?.description,
            pageLink: {
              url: cleanURL(navItem?.featuredPromo),
              text: navItem?.featuredPromo?.pageLinkLabel || navItem?.featuredPromo?.pageLink?.text
            },
            image: {
              src: navItem?.featuredPromo?.image?.image?.url,
              altText: navItem?.featuredPromo?.image?.altText
            }
          }
        };
      }
    })?.filter(Boolean);
  }, [data?.navigationItemsCollection?.items, locale, lang, isStrictLocale, isPageTranslated]);
  const openMegaMenu = useCallback((menu: string | null) => {
    setCurrentOpenMegaMenu((prev: string | null) => {
      return prev !== menu ? menu : null;
    });
  }, []);
  const closeAllMenus = useCallback(() => {
    setCurrentOpenMegaMenu(null);
    setOpenTopLevelMobileItem(null);
    setOpenSecondLevelMobileItem(null);
    setCurrentOpenSubCategory(null);
    setIsSubCategoryOpen(false);
    setWaffleMenuDeployed(false);
  }, []);
  useEffect(() => {
    setMobileNavOpen(false);
    closeAllMenus();
    // Reset scroll-hidden state on route change so the new page (which may
    // be a Page: Landing with an inverted rule, or a standard page) starts
    // with the nav visible and a clean scroll baseline.
    setIsHidden(false);
    setLastPosition(0);
  }, [pathname, closeAllMenus]);
  const openMegaMenuWithKeys = useCallback((event: any, id: string) => {
    if (mouse === false) {
      // checks that a user hits the enter key to open the modal
      if (event?.keyCode === 13) {
        openMegaMenu(id);
      } else if (event.keyCode === 27) {
        closeAllMenus();
      }
    }
  }, [mouse, closeAllMenus]);
  const handleTopLevelCategoryClick = useCallback((id: string) => {
    openMegaMenu(id);
    setIsSubCategoryOpen(false);
    setCurrentOpenSubCategory(null);
  }, [openMegaMenu, currentOpenMegaMenu]);
  const handleOpenNavItem = (itemId: string) => {
    if (itemId === openTopLevelMobileItem) {
      setOpenTopLevelMobileItem(null);
    } else {
      setOpenTopLevelMobileItem(itemId);
    }
  };
  const onLanguageChange = useCallback((url: string) => {
    router.replace(url);
  }, [router]);
  const formattedSiteNavData = {
    navItems: formattedMainNavItems,
    navCta: navCtaData,
    currentOpenMegaMenu,
    setCurrentOpenMegaMenu,
    mobileNavOpen,
    setMobileNavOpen,
    isSubCategoryOpen,
    setIsSubCategoryOpen,
    currentOpenSubCategory,
    setCurrentOpenSubCategory,
    navMinHeight,
    setNavMinHeight,
    handleTopLevelCategoryClick,
    handleOpenNavItem,
    openMegaMenuWithKeys,
    openTopLevelMobileItem,
    openSecondLevelMobileItem,
    setOpenSecondLevelMobileItem,
    setOpenTopLevelMobileItem,
    closeAllMenus,
    languageSelectorData: {
      labels: {
        language: config?.commonDefaultValues?.labelForLanguageSwitcher || "LANGUAGE"
      },
      languages: siteLangs,
      currentLangCode: lang,
      currentPath: pathname,
      onLanguageChange: onLanguageChange
    }
  };
  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement || hasSearchActivated) return;

    // Terra's actual search trigger markup (e.g.
    // ".t2-site-nav-top__utility-nav-search") is internal to the trimble-terra
    // package and can change between versions, so we don't hard-code its
    // selector here — a previous attempt to do so silently broke nav search
    // when the guessed class names didn't match Terra's real DOM. Instead we
    // preload eagerly on any pointerdown/focusin inside the nav (cheap: it's
    // a no-op once the chunk is cached) and rely on the searchDeployed/
    // desktopSearchDeployed state effect below — which Terra always drives
    // via the setters we hand it — as the source of truth for activation.
    const handlePointerDown = () => {
      preloadNavSearch();
    };
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.matches("input, textarea") && navElement.contains(target)) {
        preloadNavSearch();
      }
    };
    navElement.addEventListener("pointerdown", handlePointerDown, {
      passive: true
    });
    navElement.addEventListener("focusin", handleFocusIn);
    return () => {
      navElement.removeEventListener("pointerdown", handlePointerDown);
      navElement.removeEventListener("focusin", handleFocusIn);
    };
  }, [hasSearchActivated, preloadNavSearch]);

  // Source of truth for activation: Terra drives searchDeployed/
  // desktopSearchDeployed itself (e.g. clicking its search icon calls its
  // internal toggleDesktopSearch, which calls the setters we pass it below).
  // Activating off this state — rather than guessing at Terra's internal DOM
  // structure/selectors — means nav search keeps working even if Terra's
  // markup changes.
  useEffect(() => {
    if (!hasSearchActivated && (searchDeployed || desktopSearchDeployed)) {
      activateNavSearch();
    }
  }, [hasSearchActivated, searchDeployed, desktopSearchDeployed, activateNavSearch]);
  const navSearchElement = hasSearchActivated ? <NavSearch data={rawAppData} searchDeployed={searchDeployed} commonSearches={rawAppData?.commonSearches || []} locale={locale} globalSearch={false} navigationData={formattedSiteNavData} clearSearch={clearSearch} currentRefinement={currentRefinement} setCurrentRefinement={setCurrentRefinement} setSearchQuery={setSearchQuery} searchFilter={activeSearchFilter} searchIndex={activeSearchIndex} onClearFilter={clearSearchFilter} onRemoveToken={removeSearchToken} isStrictLocale={isStrictLocale} /> : <></>;
  const formattedWaffleLinks = data?.waffleMenuLinksCollection?.items?.map((item: any) => {
    return {
      title: item?.title,
      description: item?.description,
      linkText: item?.linkText,
      externalUrl: item?.externalUrl
    };
  });

  // `waffleMenuCta` is a Contentful `Link` entry (the generic `link` content
  // type, fetched via `LinkFragment` in `@/queries/atoms/linkFragment`), not a
  // page reference — resolve its href with the matching helper rather than
  // `cleanURL`/`pageLink`, which expect a different (page-reference) shape.
  const waffleCtaUrl = data?.waffleMenuCta ? resolveImageLinkHref(data.waffleMenuCta, lang) : null;
  const formattedWaffleCta = waffleCtaUrl ? {
    title: data.waffleMenuCta?.title || data.waffleMenuCta?.text,
    url: waffleCtaUrl
  } : undefined;
  const formattedWaffleMenuData = {
    links: formattedWaffleLinks,
    cta: formattedWaffleCta,
    waffleMenuDeployed,
    setWaffleMenuDeployed,
    closeAllDropDowns: closeAllMenus
  };
  const formattedUtilityNavMenuData = {
    languageSelectorData: formattedSiteNavData?.languageSelectorData,
    setLanguageSelectorDeployed: setLanguageSelectorDeployed,
    languageSelectorDeployed: languageSelectorDeployed,
    handleLanguageSelectorDeployed: handleLanguageSelectorDeployed,
    disableFlyout: data?.disableSectorFlyOut,
    handleSectorFlyoutDeployed: handleSectorFlyoutDeployed,
    searchDeployed: searchDeployed,
    setSearchDeployed: setSearchDeployed,
    desktopSearchDeployed: desktopSearchDeployed,
    setDesktopSearchDeployed: setDesktopSearchDeployed,
    sectorFlyoutDeployed: isSectorFlyoutDeployed,
    sectorFlyoutData: {
      ...sectorFlyoutData,
      // accountSwitcher: accountSwitcher,
      setDesktopSearchDeployed: setDesktopSearchDeployed,
      setGlobalSearch: setGlobalSearch
    },
    waffleMenuData: formattedWaffleMenuData,
    onSearch: onSearch as unknown as () => void,
    searchPlaceholderText: currentRefinement,
    isAuthenticated: isAuthenticated,
    disableCartFlyout: flyoutCart?.isDisabled,
    cartData: flyoutCart?.cartData,
    cartSettings: flyoutCart?.cartSettings,
    isCartFlyoutOpen: flyoutCart?.isCartFlyoutOpen,
    onCartFlyoutOpen: flyoutCart?.onCartFlyoutOpen,
    onCartRemoveItem: flyoutCart?.onCartRemoveItem,
    onCartChangeQuantity: flyoutCart?.onCartChangeQuantity,
    currencies: flyoutCart?.currencies,
    defaultCurrency: flyoutCart?.defaultCurrency,
    activeCurrency: flyoutCart?.activeCurrency,
    setCurrency: flyoutCart?.setCurrency,
    isCurrencyLocked: flyoutCart?.isCurrencyLocked,
    searchComponent: navSearchElement as any,
    setGlobalSearch: setGlobalSearch,
    globalSearch: globalSearch
  };
  const renderLogoUrl = (logo: any) => {
    if (logo?.__typename == "BynderAsset") {
      return logo?.bynderAssetReference?.[0]?.src;
    } else if (logo?.image?.url) {
      return logo?.image?.url;
    } else {
      return null;
    }
  };

  // Logo accessible name. Terra's SiteNav renders the logo as
  // <img alt={altText || ""} aria-label="Go to Trimble Inc. Home Page" />.
  // If altText is empty the <img> is treated as presentational (decorative),
  // which contradicts having an aria-label and breaks the accessibility tree
  // (Lighthouse: "Elements marked as presentational should be consistently
  // ignored"). Provide a non-empty fallback so the image always has a name.
  const defaultLogoAltText = "Trimble";

  // altText lives at the top level of the Image / BynderAsset content type
  // (see atoms/imageFragment.ts and fragments/bynderAssetFragment.ts), not
  // inside the nested `image` asset.
  const mainLogo = {
    src: renderLogoUrl(data?.logo) || "https://images.ctfassets.net/citn2sn5tdjr/5K3bjaD0rAHfTBLJnBOG0F/4724f023136ec940a6d828b363466b9b/TrimbleR-Horiz-RGB-_White.svg",
    url: `/${locale}`,
    // baseUrl,
    altText: data?.logo?.altText || defaultLogoAltText
  };
  const alternativeLogo = {
    src: renderLogoUrl(data?.alternativeLogo),
    url: `/${locale}`,
    // baseUrl,
    altText: data?.alternativeLogo?.altText || defaultLogoAltText
  };
  const logoVariant = data?.logoVariant || "Standard";
  const logoToUse = logoVariant === "Alternative" ? alternativeLogo : mainLogo;
  const closeAllDropDowns = useCallback((e: any | null = null) => {
    if (!navRef?.current?.contains(e?.target)) {
      setCurrentOpenMegaMenu(null);
      setIsSubCategoryOpen(false);
      setCurrentOpenSubCategory(null);
    }
  }, []);

  // Page: Landing entries opt into an alternate scroll rule that inverts the
  // sitewide default. The marker component (LandingPageBehaviorMarker) sets
  // `document.documentElement.dataset.pageBehavior = "landing"` when a
  // pageLanding route mounts. We read it fresh on every scroll tick so
  // client-side navigations between landing and non-landing pages pick up
  // the correct rule without needing to re-render the nav.
  const LANDING_TOP_THRESHOLD = 8;
  const checkScroll = useCallback(() => {
    if (mobileNavOpen) return;
    const position = window?.scrollY ?? 0;
    const isLandingPage = typeof document !== "undefined" && document.documentElement.dataset.pageBehavior === "landing";
    if (isLandingPage) {
      // Landing rule
      //   • At the absolute top of the page: nav shown (`position: fixed`).
      //   • Any scroll past the top: nav flips to `--is-hidden`
      //     (`position: absolute`) so it scrolls out of view with the page.
      //   • Scrolling back up does NOT bring the nav back; it stays hidden
      //     through any further scroll motion in either direction and only
      //     reappears once the user is back at the top of the page.
      if (position <= LANDING_TOP_THRESHOLD) {
        setIsHidden(false);
        setLastPosition(position);
        return;
      }
      setIsHidden(true);
      setLastPosition(position);
      setCurrentOpenMenu(null);
      setCurrentOpenMegaMenu(null);
      setIsSubCategoryOpen(false);
      setCurrentOpenSubCategory(null);
      return;
    }

    // Default rule (unchanged): scroll-down > 100px hides the nav,
    // scroll-up shows it again.
    if (position > lastPositionRef.current + 100) {
      setIsHidden(true);
      setLastPosition(position);
      setCurrentOpenMenu(null);
      setCurrentOpenMegaMenu(null);
      setIsSubCategoryOpen(false);
      setCurrentOpenSubCategory(null);
    } else if (position < lastPositionRef.current) {
      setIsHidden(false);
      setLastPosition(position);
    }
  }, [mobileNavOpen]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.addEventListener("click", closeAllDropDowns);
    window.addEventListener("scroll", checkScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener("click", closeAllDropDowns);
      window.removeEventListener("scroll", checkScroll);
    };
  }, [closeAllDropDowns, checkScroll]);

  // Patch Terra's language-selector header with role="button" so aria-expanded is valid
  useEffect(() => {
    const el = navRef.current?.querySelector(".t2-language-selector__header");
    if (el && !el.getAttribute("role")) {
      el.setAttribute("role", "button");
    }
  }, []);

  // Patch Terra's logo <img>: Terra renders it with both alt="" (presentational)
  // and aria-label, which is contradictory and fails Lighthouse's "well-formed
  // accessibility tree" check. Promote aria-label to alt and drop the
  // redundant aria-label so the image has a single, consistent accessible name.
  useEffect(() => {
    const img = navRef.current?.querySelector(".t2-site-nav__logo-image") as HTMLImageElement | null;
    if (!img) return;
    const ariaLabel = img.getAttribute("aria-label");
    if (ariaLabel) {
      if (!img.getAttribute("alt")) {
        img.setAttribute("alt", ariaLabel);
      }
      img.removeAttribute("aria-label");
    }
  }, [logoToUse?.src, logoToUse?.altText]);
  const siteName = process.env.NEXT_PUBLIC_PROFILE == "preview" ? "Preview" : data?.sectorName || null;
  return <header className={`site-navigation site-navigation${isHidden && currentOpenMegaMenu === null ? "--is-hidden" : "--is-showing"}`} ref={navRef}>
      <SiteNav mainNavMenu={formattedSiteNavData} logo={logoToUse} logoVariant={logoVariant} sectorName={siteName} utilityNavMenu={formattedUtilityNavMenuData} LinkComponent={NoPrefetchNavLink} />
    </header>;
}
export { cleanURLWithLang } from "@/lib/url/cleanURLWithLang";